/**
 * Stacked bars (Recharts): California GHG by county or city from ghg_inventory_precalculated.
 * AreaChart uses axis tooltips (all geographies at once); BarChart uses item tooltips so only
 * the hovered place shows in the tooltip. Custom content enforces a single payload row.
 */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { TooltipProps } from 'recharts';
import {
  PUBLICATION_GHG_CHART_YEAR_MIN,
  PUBLICATION_GHG_STACKED_TIMESERIES_YEAR_MAX,
} from '../../config/publicationGhgChartYears';
import { PUBLICATION_ANALYSIS_CHART_IDS } from '../../config/publicationAnalysisChartIds';
import { ChartPngPageIdOverrideContext, useChartPngRegistryActions } from '../../contexts/ChartPngRegistryContext';
import { PublicationFigureDownloads } from '../publication/PublicationFigureDownloads';
import {
  CA_STACKED_OTHER_CITIES_COUNT,
  CALIFORNIA_GEO_STACKED_SECTOR_OPTIONS,
  fetchCaliforniaGeoStackedInventory,
  type CaliforniaGeoStackedGeography,
  type CaliforniaGeoStackedSector,
} from '../../services/californiaGhgGeographyStackedService';
import { downloadDataAsCSV, downloadDomAsPNG, formatFilename } from '../../utils/downloadUtils';

type GeoStackedNarrativeSlice = {
  stackKeys: string[];
  rows: Record<string, string | number>[];
  otherBucketLabel: string | null;
};

function yearlyTotalsMt(slice: GeoStackedNarrativeSlice): Record<number, number> {
  const out: Record<number, number> = {};
  for (const row of slice.rows) {
    const y = Number(row.year);
    if (!Number.isFinite(y)) continue;
    let s = 0;
    for (const k of slice.stackKeys) {
      const v = row[k];
      if (typeof v === 'number' && Number.isFinite(v)) s += v;
    }
    out[y] = s;
  }
  return out;
}

/** Top five stack segments in a year (excludes “Other” city bucket); denominator is sum of non-Other keys. */
function topFiveGeoShareInYear(
  slice: GeoStackedNarrativeSlice,
  year: number
): { names: string[]; pct: number; stateTotal: number } | null {
  const row = slice.rows.find((r) => Number(r.year) === year);
  if (!row) return null;
  const exclude = slice.otherBucketLabel;
  const keys = slice.stackKeys.filter((k) => k !== exclude);
  let stateTotal = 0;
  const pairs: [string, number][] = [];
  for (const k of keys) {
    const v = Number(row[k]);
    const mt = Number.isFinite(v) ? v : 0;
    stateTotal += mt;
    pairs.push([k, mt]);
  }
  if (stateTotal <= 0) return null;
  pairs.sort((a, b) => b[1] - a[1]);
  const top = pairs.slice(0, 5);
  const topSum = top.reduce((acc, x) => acc + x[1], 0);
  const names = top.map(([label]) => label.replace(/\s*\(\d+\)\s*$/, '').trim());
  return { names, pct: (topSum / stateTotal) * 100, stateTotal };
}

function formatMmt(mt: number): string {
  return (mt / 1e6).toLocaleString(undefined, { maximumFractionDigits: 1, minimumFractionDigits: 1 });
}

/** Whole-number MMT for short parentheticals (e.g. “256 MMT”). */
function formatMmtWhole(mt: number): string {
  return Math.round(mt / 1e6).toLocaleString();
}

function pickTop5ReferenceYear(years: number[]): number | null {
  const yset = new Set(years);
  for (const y of [2023, 2022, 2021, 2020, 2019, 2018]) {
    if (yset.has(y)) return y;
  }
  return years.length ? years[years.length - 1]! : null;
}

function sectorNarrativePhrase(sector: CaliforniaGeoStackedSector): string {
  switch (sector) {
    case 'total':
      return 'economy-wide inventoried emissions (all sectors combined)';
    case 'transportation':
      return 'transportation-related emissions';
    case 'electricity':
      return 'electricity-sector emissions';
    case 'commercial_residential':
      return 'commercial and residential natural gas emissions';
    case 'agriculture':
      return 'agriculture emissions';
    case 'waste_other':
      return 'waste and other emissions';
    default:
      return 'emissions';
  }
}

function formatMtTick(v: number): string {
  if (!Number.isFinite(v)) return '';
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(0)}k`;
  return String(Math.round(v));
}

function colorForStackKey(key: string, index: number, otherLabel: string | null): string {
  if (otherLabel && key === otherLabel) return '#64748b';
  const hue = (index * 47 + 18) % 360;
  return `hsl(${hue}, 46%, 44%)`;
}

/** One geography per tooltip; Recharts may still pass multiple payload rows in edge cases. */
function CaliforniaGeoStackedTooltip({
  active,
  payload,
  label,
}: TooltipProps<number, string>) {
  if (!active || !payload?.length) return null;
  const row = payload[0];
  const name = String(row.dataKey ?? row.name ?? '—');
  const raw = row.value;
  const n = typeof raw === 'number' ? raw : Number(raw);
  const valueStr = Number.isFinite(n) ? `${Math.round(n).toLocaleString()} Mt CO₂e` : '—';
  const fill = row.color && typeof row.color === 'string' ? row.color : '#334155';

  return (
    <div
      className="recharts-default-tooltip"
      style={{
        margin: 0,
        padding: '10px 12px',
        background: 'rgba(255, 255, 255, 0.96)',
        border: '1px solid #e2e8f0',
        borderRadius: 6,
        boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
        fontSize: 12,
      }}
    >
      <p style={{ margin: '0 0 6px', fontWeight: 600, color: '#0f172a' }}>{`Year ${label}`}</p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span
          aria-hidden
          style={{
            width: 10,
            height: 10,
            borderRadius: 2,
            background: fill,
            flexShrink: 0,
          }}
        />
        <span style={{ color: '#334155' }}>
          <strong>{name}</strong>
          <span style={{ marginLeft: 6 }}>{`: ${valueStr}`}</span>
        </span>
      </div>
    </div>
  );
}

export const CaliforniaGhgTimeSeriesStackedChart: React.FC = () => {
  const figureRef = useRef<HTMLDivElement>(null);
  const pngPageOverride = useContext(ChartPngPageIdOverrideContext);
  const pngPageId = pngPageOverride ?? 'statewide_analysis';
  const { register: registerPng, unregister: unregisterPng } = useChartPngRegistryActions();

  const [geography, setGeography] = useState<CaliforniaGeoStackedGeography>('counties');
  const [sector, setSector] = useState<CaliforniaGeoStackedSector>('total');
  const [showLegend, setShowLegend] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [model, setModel] = useState<{
    stackKeys: string[];
    rows: Record<string, string | number>[];
    otherBucketLabel: string | null;
  }>({ stackKeys: [], rows: [], otherBucketLabel: null });
  /** Stacked rows for summary copy (counties or cities, matching the selected geography). */
  const [narrativeSlice, setNarrativeSlice] = useState<GeoStackedNarrativeSlice | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const yearMin = PUBLICATION_GHG_CHART_YEAR_MIN;
    const yearMax = PUBLICATION_GHG_STACKED_TIMESERIES_YEAR_MAX;
    const countyPromise = fetchCaliforniaGeoStackedInventory({
      geography: 'counties',
      sector,
      yearMin,
      yearMax,
    });
    const chartPromise =
      geography === 'counties'
        ? countyPromise
        : fetchCaliforniaGeoStackedInventory({
            geography: 'cities',
            sector,
            yearMin,
            yearMax,
          });

    const [countyRes, chartRes] = await Promise.all([countyPromise, chartPromise]);

    if (chartRes.error) {
      setError(chartRes.error);
      setModel({ stackKeys: [], rows: [], otherBucketLabel: null });
      setNarrativeSlice(null);
      setLoading(false);
      return;
    }

    setModel({
      stackKeys: chartRes.stackKeys,
      rows: chartRes.rows,
      otherBucketLabel: chartRes.otherBucketLabel,
    });

    const narrativeSource = geography === 'counties' ? countyRes : chartRes;
    if (!narrativeSource.error && narrativeSource.rows.length > 0) {
      setNarrativeSlice({
        stackKeys: narrativeSource.stackKeys,
        rows: narrativeSource.rows,
        otherBucketLabel: narrativeSource.otherBucketLabel,
      });
    } else {
      setNarrativeSlice(null);
    }

    setLoading(false);
  }, [geography, sector]);

  useEffect(() => {
    void load();
  }, [load]);

  const narrativeBlock = useMemo(() => {
    if (!narrativeSlice || narrativeSlice.rows.length === 0) return null;
    const totals = yearlyTotalsMt(narrativeSlice);
    const yearsSorted = Object.keys(totals)
      .map(Number)
      .filter((y) => Number.isFinite(y) && totals[y]! > 0)
      .sort((a, b) => a - b);
    if (yearsSorted.length === 0) return null;

    const t2023 = totals[2023];
    const t2005 = totals[2005];
    const has2023 = t2023 != null && Number.isFinite(t2023) && t2023 > 0;
    const has2005 = t2005 != null && Number.isFinite(t2005) && t2005 > 0;

    const declineEndYear =
      [2023, 2022, 2021, 2020].find((y) => totals[y] != null && Number.isFinite(totals[y]) && totals[y]! > 0) ??
      yearsSorted[yearsSorted.length - 1]!;
    const tEnd = totals[declineEndYear];
    const changeVs2005Pct =
      has2005 && tEnd != null && Number.isFinite(tEnd) && tEnd > 0 && t2005! > 0
        ? ((t2005! - tEnd) / t2005!) * 100
        : null;

    const refTop5Year = pickTop5ReferenceYear(yearsSorted);
    const top5 = refTop5Year != null ? topFiveGeoShareInYear(narrativeSlice, refTop5Year) : null;
    const listFmt = new Intl.ListFormat('en', { style: 'long', type: 'conjunction' });

    const sectorPhrase = sectorNarrativePhrase(sector);
    const isCities = geography === 'cities';

    return (
      <p className="publication-ca-ghg-ts-narrative" id="publication-ca-ghg-ts-summary">
        For <strong>{sectorPhrase}</strong>,{' '}
        {isCities ? (
          <>
            inventoried <strong>California cities</strong> in this chart (each labeled city plus an &quot;Other&quot;
            bucket that combines the smallest <strong>{CA_STACKED_OTHER_CITIES_COUNT}</strong> places by cumulative
            emissions over {PUBLICATION_GHG_CHART_YEAR_MIN}–{PUBLICATION_GHG_STACKED_TIMESERIES_YEAR_MAX}) together
            total about{' '}
          </>
        ) : (
          <>
            <strong>California counties</strong> together total about{' '}
          </>
        )}
        {has2023 ? (
          <>
            <strong>{formatMmt(t2023!)}</strong> million metric tons (MMT) CO₂e in <strong>2023</strong>
          </>
        ) : (
          <>
            <strong>—</strong> MMT CO₂e in <strong>2023</strong> (not in this year range)
          </>
        )}
        .
        {changeVs2005Pct != null && (
          <>
            {' '}
            Emissions in <strong>{declineEndYear}</strong> were{' '}
            {changeVs2005Pct > 0.05 ? (
              <>
                about <strong>{changeVs2005Pct.toFixed(1)}%</strong> below 2005 levels
              </>
            ) : changeVs2005Pct < -0.05 ? (
              <>
                about <strong>{Math.abs(changeVs2005Pct).toFixed(1)}%</strong> above 2005 levels
              </>
            ) : (
              <>roughly even with 2005 levels</>
            )}
            {has2005 ? (
              <>
                {' '}
                (<strong>{formatMmtWhole(t2005!)}</strong> MMT).
              </>
            ) : (
              <>.</>
            )}
          </>
        )}
        {top5 && top5.names.length === 5 && refTop5Year != null && (
          <>
            {' '}
            About <strong>{top5.pct.toFixed(1)}%</strong>{' '}
            {isCities ? (
              <>
                of combined emissions from <strong>individually charted cities</strong> for this sector in{' '}
                <strong>{refTop5Year}</strong> came from the five largest totals (
                {listFmt.format(top5.names)}; &quot;Other&quot; bucket excluded).
              </>
            ) : (
              <>
                of the summed county total for this sector in <strong>{refTop5Year}</strong> came from the five
                largest counties ({listFmt.format(top5.names)}).
              </>
            )}
          </>
        )}
      </p>
    );
  }, [narrativeSlice, sector, geography]);

  const sectorLabel = useMemo(
    () => CALIFORNIA_GEO_STACKED_SECTOR_OPTIONS.find((o) => o.value === sector)?.label ?? sector,
    [sector]
  );

  const handleDownloadCsv = useCallback(() => {
    if (!model.rows.length) return;
    const rows = model.rows.map((r) => ({
      geography,
      sector: sectorLabel,
      ...r,
    }));
    downloadDataAsCSV(
      rows,
      formatFilename('California', `GHG_Stacked_${geography}_${sector}`)
    );
  }, [geography, sector, sectorLabel, model.rows]);

  const handleDownloadPng = useCallback(() => {
    return downloadDomAsPNG(
      figureRef.current,
      formatFilename('California', `GHG_Stacked_${geography}_${sector}`)
    );
  }, [geography, sector]);

  useEffect(() => {
    const id = PUBLICATION_ANALYSIS_CHART_IDS.ghgStacked;
    const label = 'California GHG inventory (stacked by geography)';
    registerPng(id, label, () => void handleDownloadPng(), pngPageId);
    return () => unregisterPng(id);
  }, [registerPng, unregisterPng, handleDownloadPng, pngPageId]);

  if (loading && model.rows.length === 0) {
    return (
      <div className="publication-ca-ghg-ts publication-ca-ghg-ts--initial-loading" role="status" aria-live="polite">
        <div className="publication-ca-ghg-ts-spinner" aria-hidden />
        <p className="publication-ca-ghg-ts-loading-text">Loading inventory…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="publication-ca-ghg-ts">
        <p className="publication-scatter-error">Could not load chart: {error}</p>
      </div>
    );
  }

  if (!loading && model.rows.length === 0) {
    return (
      <div className="publication-ca-ghg-ts">
        <p className="publication-scatter-placeholder">
          No rows for this geography and year range. Run <code>npm run populate-ghg-inventory</code> for
          California cities and counties.
        </p>
      </div>
    );
  }

  return (
    <div className="publication-ca-ghg-ts">
      <div className="publication-ca-ghg-ts-controls publication-county-ghg-controls">
        <label className="publication-scatter-field">
          <span>Geography</span>
          <select
            value={geography}
            onChange={(e) => setGeography(e.target.value as CaliforniaGeoStackedGeography)}
            disabled={loading}
            aria-label="Stacked chart geography"
          >
            <option value="cities">Cities</option>
            <option value="counties">Counties</option>
          </select>
        </label>
        <label className="publication-scatter-field">
          <span>Sector</span>
          <select
            value={sector}
            onChange={(e) => setSector(e.target.value as CaliforniaGeoStackedSector)}
            disabled={loading}
            aria-label="Stacked chart sector"
          >
            {CALIFORNIA_GEO_STACKED_SECTOR_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      {narrativeBlock}
      {model.otherBucketLabel && (
        <p className="publication-ca-ghg-ts-meta">
          Cities: smallest {CA_STACKED_OTHER_CITIES_COUNT} places by cumulative Mt for this sector over{' '}
          {PUBLICATION_GHG_CHART_YEAR_MIN}–{PUBLICATION_GHG_STACKED_TIMESERIES_YEAR_MAX} are combined into{' '}
          <strong>{model.otherBucketLabel}</strong>.
        </p>
      )}
      <div className="publication-figure-frame publication-ca-ghg-ts-figure">
        <div ref={figureRef} className="publication-ca-ghg-recharts-wrap" id="publication-ca-ghg-ts-chart">
          {loading && (
            <div className="publication-ca-ghg-ts-loading" role="status" aria-live="polite">
              <div className="publication-ca-ghg-ts-spinner publication-ca-ghg-ts-spinner--overlay" aria-hidden />
              <span className="publication-ca-ghg-ts-loading-caption">Updating…</span>
            </div>
          )}
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={model.rows}
              margin={{
                top: 8,
                right: 12,
                left: 4,
                bottom: 28,
              }}
              maxBarSize={56}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="year" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={formatMtTick} width={52} tick={{ fontSize: 10 }} />
              <Tooltip
                shared={false}
                cursor={{ fill: 'rgba(148, 163, 184, 0.12)' }}
                content={<CaliforniaGeoStackedTooltip />}
              />
              {model.stackKeys.map((key, i) => (
                <Bar
                  key={key}
                  dataKey={key}
                  stackId="inv"
                  fill={colorForStackKey(key, i, model.otherBucketLabel)}
                  fillOpacity={0.88}
                  stroke={colorForStackKey(key, i, model.otherBucketLabel)}
                  strokeWidth={0.25}
                  isAnimationActive={false}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
        {showLegend && (
          <div className="publication-ca-ghg-ts-stack-legend" aria-label="Stack legend">
            <ul className="publication-ca-ghg-ts-stack-legend-list">
              {model.stackKeys.map((key, i) => {
                const fill = colorForStackKey(key, i, model.otherBucketLabel);
                return (
                  <li key={key} className="publication-ca-ghg-ts-stack-legend-item">
                    <span
                      className="publication-ca-ghg-ts-stack-legend-swatch"
                      style={{ backgroundColor: fill }}
                      aria-hidden
                    />
                    <span className="publication-ca-ghg-ts-stack-legend-label">{key}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        <div className="publication-ca-ghg-ts-figure-actions">
          <button
            type="button"
            className="publication-county-ghg-refresh"
            onClick={() => setShowLegend((v) => !v)}
            disabled={loading}
            aria-pressed={showLegend}
            aria-controls="publication-ca-ghg-ts-chart"
          >
            {showLegend ? 'Hide legend' : 'Show legend'}
          </button>
          <PublicationFigureDownloads
            onCsv={handleDownloadCsv}
            onPng={handleDownloadPng}
            csvDisabled={loading || model.rows.length === 0}
            pngDisabled={loading || model.rows.length === 0}
            ariaLabel="Download stacked GHG chart"
          />
        </div>
      </div>
    </div>
  );
};

export default CaliforniaGhgTimeSeriesStackedChart;
